import React from "react";
import { useTranslation } from "react-i18next";
import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { useAccountAlert } from "@/features/betslip/components/UnauthorizedAlerts";
import { Selection } from "@/features/betslip/components/cards/Selection";
import { MultiCard } from "@/features/betslip/components/cards/MultiCard";
import { CombinationsCard } from "@/features/betslip/components/cards/CombinationsCard";
import { StakeAllSingles } from "@/features/betslip/components/StakeAllSingles";
import { BetslipSection } from "@/features/betslip/components/parts/BetslipSection";
import { CombosInfo } from "@/features/betslip/components/parts/CombosInfo";
import { CreateSrmCard } from "@/features/betslip/components/cards/CreateSrmCard";

export const SlipSections = () => {
  const { t } = useTranslation();
  const singles = useBetslipStore((state) => state.singlesSectionSelections);
  const sgm = useBetslipStore((state) => state.sgmSectionSelections);
  const srm = useBetslipStore((state) => state.srmSectionSelections);
  const unusedSelections = useBetslipStore((state) => state.unusedSelections);
  const showMultiCard = useBetslipStore((state) => state.showMultiCard);
  const showCombinationsSection = useBetslipStore(
    (state) => state.showCombinationsSection,
  );
  const possibleCombinationsCount = useBetslipStore(
    (state) => state.possibleCombinations.length,
  );
  const transportError = useBetslipStore((state) => state.transportError);
  const accountAlert = useAccountAlert();

  const hasSlipAlerts = Boolean(transportError) || Boolean(accountAlert);
  const singlesLabel = singles.length > 1 ? "Singles" : "Single";

  // The first visible section hangs off the tab bar as an attached banner,
  // unless slip alerts are pinned there instead. Order matches the
  // on-screen stack, top to bottom.
  const sectionOrder = [
    { id: "singles", visible: singles.length > 0 },
    { id: "sgm", visible: sgm.length > 0 },
    { id: "srm", visible: srm.length > 0 },
    { id: "multi", visible: showMultiCard },
    { id: "combinations", visible: showCombinationsSection },
    { id: "unused", visible: unusedSelections.length > 0 },
  ];
  const attachedSection = hasSlipAlerts
    ? null
    : sectionOrder.find((section) => section.visible)?.id;

  return (
    <>
      {singles.length > 0 && (
        <BetslipSection
          label={singlesLabel}
          count={singles.length}
          testId="singles-section"
          attached={attachedSection === "singles"}
        >
          <StakeAllSingles />
          {singles.map((selection) => (
            <Selection
              key={selection.outcomeId}
              outcomeId={selection.outcomeId}
            />
          ))}
        </BetslipSection>
      )}
      <CreateSrmCard />
      {sgm.length > 0 && (
        <BetslipSection
          label={t("Same Game Multi")}
          count={sgm.length}
          testId="sgm-section"
          attached={attachedSection === "sgm"}
        >
          {sgm.map((selection) => (
            <Selection
              key={selection.outcomeId}
              outcomeId={selection.outcomeId}
            />
          ))}
        </BetslipSection>
      )}
      {srm.length > 0 && (
        <BetslipSection
          label={t("Same Race Multi")}
          count={srm.length}
          testId="srm-section"
          attached={attachedSection === "srm"}
        >
          {srm.map((selection) => (
            <Selection
              key={selection.outcomeId}
              outcomeId={selection.outcomeId}
            />
          ))}
        </BetslipSection>
      )}
      {showMultiCard && (
        <BetslipSection
          label={t("Multis")}
          testId="multi-section"
          attached={attachedSection === "multi"}
        >
          <MultiCard />
        </BetslipSection>
      )}
      {showCombinationsSection && (
        <BetslipSection
          label={t("Combination Multis")}
          info={<CombosInfo />}
          count={possibleCombinationsCount}
          testId="combinations-section"
          attached={attachedSection === "combinations"}
        >
          <CombinationsCard />
        </BetslipSection>
      )}
      {unusedSelections.length > 0 && (
        <BetslipSection
          label="Unused Selections"
          count={unusedSelections.length}
          testId="unused-selections-section"
          defaultOpen={false}
          attached={attachedSection === "unused"}
        >
          {unusedSelections.map((selection) => (
            <Selection
              key={selection.outcomeId}
              outcomeId={selection.outcomeId}
            />
          ))}
        </BetslipSection>
      )}
    </>
  );
};
